import { useRef } from "react";

// Simple WAV encoder (16kHz mono 16-bit PCM) using ScriptProcessor and OfflineAudioContext for resampling
export default function useWavRecorder({ targetSampleRate = 16000 } = {}) {
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedRef = useRef(false);
  const inputSampleRateRef = useRef(null);

  const start = async () => {
    if (startedRef.current) return { stream: streamRef.current };
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const ctx = new AudioContextCtor();
    await ctx.resume();
    const source = ctx.createMediaStreamSource(stream);
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    chunksRef.current = [];
    source.connect(processor);
    processor.connect(ctx.destination);
    processor.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      // Copy the Float32 chunk for later resampling
      chunksRef.current.push(new Float32Array(input));
    };
    audioCtxRef.current = ctx;
    inputSampleRateRef.current = ctx.sampleRate; // Store the input sample rate
    processorRef.current = processor;
    sourceRef.current = source;
    streamRef.current = stream;
    startedRef.current = true;
    return { stream };
  };

  const stop = async () => {
    if (!startedRef.current) return null;
    startedRef.current = false;
    try { processorRef.current && processorRef.current.disconnect(); } catch {}
    try { sourceRef.current && sourceRef.current.disconnect(); } catch {}
    try { audioCtxRef.current && audioCtxRef.current.close(); } catch {}
    const floatData = concatFloat32Arrays(chunksRef.current);
    chunksRef.current = [];

    // Resample to targetSampleRate using OfflineAudioContext if needed
    let resampled;
    if (floatData.length === 0) {
      resampled = new Float32Array(0);
    } else {
      // Use the stored input sample rate, fallback to 48000 if not available
      const inputSampleRate = inputSampleRateRef.current || 48000;
      resampled = await resampleToTarget(floatData, inputSampleRate, targetSampleRate);
    }

    const wavBuffer = encodeWavPCM16(resampled, targetSampleRate);
    const blob = new Blob([wavBuffer], { type: 'audio/wav' });
    try { streamRef.current && streamRef.current.getTracks().forEach(t => t.stop()); } catch {}
    streamRef.current = null;
    audioCtxRef.current = null;
    inputSampleRateRef.current = null;
    processorRef.current = null;
    sourceRef.current = null;
    return { blob };
  };

  return { start, stop, getStream: () => streamRef.current };
}

function concatFloat32Arrays(chunks) {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Float32Array(total);
  let offset = 0;
  for (const c of chunks) { out.set(c, offset); offset += c.length; }
  return out;
}

async function resampleToTarget(float32, inputSampleRate, targetSampleRate) {
  if (!inputSampleRate || inputSampleRate === targetSampleRate) return float32;
  const duration = float32.length / inputSampleRate;
  const offline = new OfflineAudioContext(1, Math.ceil(duration * targetSampleRate), targetSampleRate);
  const buffer = offline.createBuffer(1, float32.length, inputSampleRate);
  buffer.copyToChannel(float32, 0, 0);
  const src = offline.createBufferSource();
  src.buffer = buffer;
  src.connect(offline.destination);
  src.start(0);
  const rendered = await offline.startRendering();
  return rendered.getChannelData(0);
}

function encodeWavPCM16(samplesFloat32, sampleRate) {
  const numChannels = 1;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataLength = samplesFloat32.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true);  // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // bits per sample
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true);

  // PCM data
  floatTo16BitPCM(view, 44, samplesFloat32);
  return buffer;
}

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

function floatTo16BitPCM(view, offset, input) {
  let pos = offset;
  for (let i = 0; i < input.length; i++, pos += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(pos, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
}

